import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import axios from 'axios';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';

// "Huoltomuistutukset" sub-tab of the Ominaisuudet (Features) settings tab.
// Only rendered for eligible orgs (service_reminders_eligible gate in
// OrganizationFeaturesTab), mirroring FeaturesPartsSalesTab: the org's own
// on/off toggle lives here. The operational views (rules, upcoming, sent)
// live in the main menu under Asiakkaat, like Asiakaspalaute, and the menu
// item only appears when the toggle is on.

function PrivacyNote() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const exampleText = t('service_reminders.privacy_example');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exampleText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  return (
    <Box sx={{ maxWidth: 640, mt: 4 }} data-testid="reminders-privacy-note">
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {t('service_reminders.privacy_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('service_reminders.privacy_text')}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1.5 }}>
          {exampleText}
        </Typography>
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
          {copied ? t('service_reminders.privacy_copied') : t('service_reminders.privacy_copy')}
        </Button>
      </Paper>
    </Box>
  );
}

export default function ServiceRemindersTab({ onDirtyChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization, fetchUser } = useUserStore();
  const [form, setForm] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!currentOrganization) return;
    setForm({
      service_reminders_enabled: currentOrganization.service_reminders_enabled || false,
    });
    setIsDirty(false);
  }, [currentOrganization?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (event) => {
    const checked = event.target.checked;
    setForm((prev) => ({ ...prev, service_reminders_enabled: checked }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      setSaving(true);
      await axios.patch(
        `/api/organizations/${currentOrganization.organization_id}/`,
        { service_reminders_enabled: form.service_reminders_enabled },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
            'X-Organization-Id': currentOrganization.organization_id,
          },
        }
      );
      // Refresh user store so nav menu and feature gates update immediately
      await fetchUser();
      setIsDirty(false);
      setSnackbar({ open: true, message: t('Organization updated successfully'), severity: 'success' });
    } catch (error) {
      console.error('Failed to update service reminders settings:', error);
      const message = error.response?.data?.detail || t('Failed to update organization');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Report dirty state to parent (sub-tab + tab-switch guards).
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(isDirty, () => handleSaveRef.current());
  }, [isDirty, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const featureActive = currentOrganization?.service_reminders_available === true;

  return (
    <Box>
      <Box sx={{ maxWidth: 640 }}>
        <FormControlLabel
          control={
            <Switch
              checked={form.service_reminders_enabled}
              onChange={handleToggle}
              data-testid="service-reminders-toggle"
            />
          }
          label={t('service_reminders.feature_toggle')}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
          {t('service_reminders.feature_toggle_help')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || !isDirty}
            data-testid="service-reminders-settings-save"
          >
            {saving ? <CircularProgress size={20} /> : <span>{t('Save Changes')}</span>}
          </Button>
        </Box>
      </Box>

      {featureActive && (
        <Box sx={{ maxWidth: 640, mt: 2 }} data-testid="service-reminders-pointer">
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <NotificationsActiveIcon sx={{ color: 'primary.main' }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('service_reminders.settings_pointer_title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('service_reminders.settings_pointer_text')}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => navigate('/service-reminders')} data-testid="open-reminders-page">
              {t('service_reminders.settings_pointer_button')}
            </Button>
          </Paper>
        </Box>
      )}

      <PrivacyNote />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
